import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, List, ListItem, ListItemIcon, ListItemText, Typography, Box, Breadcrumbs, Link, CircularProgress } from '@mui/material';
import useLocalStorageVO from '../Common/UseLocalStorageVO';
import { 
  Folder as FolderIcon, 
  InsertDriveFile as DocumentIcon, 
  ChevronRight as ChevronRightIcon, 
  ArrowBack as ChevronLeftIcon,
  Close as XMarkIcon 
} from '@mui/icons-material';
import { getFolderContents, moveFolder as apiMoveFolder, moveFile as apiMoveFile } from '../../api/driveApi';

const MoveDialog = ({
  open,
  onClose,
  itemToMove, // { uuid, name, isFolder (boolean), extension (for files), upUuid }
  onMoveComplete,
  executeQuery, // GraphQL query executor
  executeMutation // GraphQL mutation executor
}) => {
  const { loginUser } = useLocalStorageVO();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFolderUuid, setSelectedFolderUuid] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [currentFolderUuid, setCurrentFolderUuid] = useState('');
  const [currentFolderName, setCurrentFolderName] = useState("내 드라이브");
  const [loading, setLoading] = useState(false);

  // Load folder contents
  const loadFolderContents = async (folderUuid, folderName = '내 드라이브') => {
    setLoading(true);
    try {
      // MoveDialog는 항상 내 드라이브 컨텍스트이므로 shared: false
      const data = await getFolderContents(executeQuery, folderUuid === '' ? null : folderUuid, false);

      // Separate folders and files
      const folders = data.filter(item => !item.extension);
      const files = data.filter(item => item.extension);

      setFolders(folders);
      setFiles(files);
      setCurrentFolderUuid(folderUuid);

      // 폴더 이름 설정 - upUuid가 있고 folderName이 '현재 위치'인 경우 실제 폴더 이름 찾기
      if (folderName === '현재 위치' && folderUuid && data.length > 0) {
        // 부모 폴더 정보가 있는지 확인하여 실제 폴더 이름 설정
        // 만약 부모 폴더 정보가 없다면 '현재 폴더'로 표시
        setCurrentFolderName('현재 폴더');
      } else {
        setCurrentFolderName(folderName);
      }
    } catch (error) {
      console.error('Error loading folder contents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize dialog when opened
  useEffect(() => {
    if (open && itemToMove) {
      setSelectedFolderUuid(null);
      setNavigationHistory([]);
      // upUuid가 null이면 루트, 아니면 해당 폴더로 시작
      const initialFolder = itemToMove.upUuid || '';
      const initialFolderName = itemToMove.upUuid ? '현재 위치' : '내 드라이브';

      loadFolderContents(initialFolder, initialFolderName);
    }
  }, [open]);

  // Material-UI Dialog handles clicking outside automatically

  // Handle folder navigation
  const handleFolderClick = (folder) => {
    // Add current folder to navigation history
    setNavigationHistory(prev => [...prev, {
      uuid: currentFolderUuid,
      name: currentFolderName
    }]);
    
    // Navigate to clicked folder
    loadFolderContents(folder.uuid, folder.name);
    setSelectedFolderUuid(null); // Reset selection when navigating
  };

  // Handle going back to previous folder
  const handleGoBack = () => {
    if (navigationHistory.length > 0) {
      const previousFolder = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory(prev => prev.slice(0, -1));
      loadFolderContents(previousFolder.uuid, previousFolder.name);
      setSelectedFolderUuid(null);
    }
  };

  // Handle folder selection for moving
  const handleFolderSelect = (folderUuid) => {
    setSelectedFolderUuid(folderUuid);
  };

  // Handle move confirmation
  const handleMove = async () => {
    if (selectedFolderUuid === null) {
      return;
    }
    if (!itemToMove) {
      return;
    }

    // 현재 위치와 이동할 위치가 같은지 확인
    const currentParentUuid = itemToMove.upUuid || '';

    if (currentParentUuid === selectedFolderUuid) {
      // 같은 위치로 이동하려는 경우 팝업만 닫기
      onClose();
      return;
    }

    setLoading(true);
    try {
      if (itemToMove.isFolder) {
        // For folder move, create request object with upUuid as target
        const req = {
          uuid: itemToMove.uuid,
          parentUuid: selectedFolderUuid === '' ? null : selectedFolderUuid
        };
        await apiMoveFolder(executeMutation, req);
      } else {
        // For file move, create request object with upUuid as target
        const req = {
          uuid: itemToMove.uuid,
          parentUuid: selectedFolderUuid === '' ? null : selectedFolderUuid
        };
        await apiMoveFile(executeMutation, req);
      }

      onMoveComplete?.();
      onClose();
    } catch (error) {
      console.error('Error moving item:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get file icon based on extension
  const getFileIcon = (extension) => {
    // You can customize this based on file types
    return DocumentIcon;
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: 600, maxHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6">
          "{itemToMove?.name}" 문서 이동
        </Typography>
        <IconButton onClick={onClose} size="small">
          <XMarkIcon />
        </IconButton>
      </DialogTitle>

      {/* Navigation */}
      <Box sx={{ px: 3, py: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {currentFolderName}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, height: 400, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{
            py: 0,
            height: '100%',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            }
          }}>
            {/* Option to go to parent folder - 사용자의 최상단 폴더가 아닐 때만 표시 */}
            {currentFolderUuid !== '' && currentFolderUuid !== loginUser?.compCd && (
              <ListItem
                button
                onClick={() => {
                  if (navigationHistory.length > 0) {
                    handleGoBack();
                  } else {
                    // Root로 이동
                    loadFolderContents('', '내 드라이브');
                    setSelectedFolderUuid(null);
                  }
                }}
                sx={{
                  '&:hover': { bgcolor: 'grey.100' },
                  borderBottom: '1px solid #f0f0f0'
                }}
              >
                <ListItemIcon>
                  <ChevronLeftIcon sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="상위 폴더로 이동"
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium', color: 'primary.main' }}
                />
              </ListItem>
            )}

            {/* Option to select current folder */}
            <ListItem
              button
              selected={selectedFolderUuid === currentFolderUuid}
              onClick={() => handleFolderSelect(currentFolderUuid)}
              sx={{
                bgcolor: selectedFolderUuid === currentFolderUuid ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
                '&:hover': { bgcolor: 'grey.100' },
                '&.Mui-selected': { bgcolor: 'rgba(25, 118, 210, 0.12)' },
                '&.Mui-selected:hover': { bgcolor: 'rgba(25, 118, 210, 0.20)' }
              }}
            >
              <ListItemIcon>
                <FolderIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary={`현재 위치 (${currentFolderName})`}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
              />
            </ListItem>

            {/* Folders */}
            {folders.map((folder) => (
              <ListItem
                key={folder.uuid}
                button
                selected={selectedFolderUuid === folder.uuid}
                onClick={() => handleFolderSelect(folder.uuid)}
                onDoubleClick={() => handleFolderClick(folder)}
                sx={{
                  bgcolor: selectedFolderUuid === folder.uuid ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
                  '&:hover': { bgcolor: 'grey.100' },
                  '&.Mui-selected': { bgcolor: 'rgba(25, 118, 210, 0.12)' },
                  '&.Mui-selected:hover': { bgcolor: 'rgba(25, 118, 210, 0.20)' }
                }}
              >
                <ListItemIcon>
                  <FolderIcon sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary={folder.name}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
                <ChevronRightIcon sx={{ color: 'text.secondary' }} />
              </ListItem>
            ))}

            {/* Files (disabled/grayed out) */}
            {files.map((file) => {
              const FileIcon = getFileIcon(file.extension);
              return (
                <ListItem
                  key={file.uuid}
                  disabled
                  sx={{ opacity: 0.5 }}
                >
                  <ListItemIcon>
                    <FileIcon sx={{ color: 'text.disabled' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={file.name}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.disabled' }}
                  />
                </ListItem>
              );
            })}

            {/* Empty state */}
            {folders.length === 0 && files.length === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  This folder is empty
                </Typography>
              </Box>
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: 'grey.50', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {selectedFolderUuid !== null ? (
              selectedFolderUuid === currentFolderUuid 
                ? `대상 폴더: ${currentFolderName}`
                : `대상 폴더: ${folders.find(f => f.uuid === selectedFolderUuid)?.name || 'Unknown'}`
            ) : (
              '이동할 폴더를 선택하세요'
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={onClose}
              variant="contained"
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                '&:hover': { bgcolor: 'error.dark' }
              }}
            >
              취소
            </Button>
            <Button
              onClick={handleMove}
              disabled={(selectedFolderUuid === null) || loading}
              variant="contained"
            >
              {loading ? '이동 중...' : '이동'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MoveDialog;